import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../base/pd-base-ui-input.js";
import { PdBaseInputElement } from "../base/pd-base-input-element.js";
import { css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import "@progressive-development/pd-icon/pd-icon";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-input-file/PdInputFile.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* File upload input component.
*
* @tagname pd-input-file
* @summary File upload input with type filtering and form integration.
*
* @event input-icon-click - Fired when the input icon is clicked.
* @event validate-form - Fired when file selection changes for validation.
* @event field-change - Fired when file selection changes.
*
* @cssprop --pd-input-field-width - Input width. Default: `300px`.
* @cssprop --pd-input-field-height - Input height. Default: `2.2rem`.
* @cssprop --pd-input-field-bg-col - Background color. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-input-field-border - Border style. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-input-field-border-bottom - Bottom border. Default: `2px solid var(--pd-default-col)`.
* @cssprop --pd-input-icon-size - Icon size. Default: `calc(var(--pd-input-field-height) * 0.9)`.
* @cssprop --pd-input-icon-color - Icon color. Default: `var(--pd-default-col)`.
*/
var PdInputFile = class extends PdBaseInputElement {
	static {
		this.styles = [PdBaseInputElement.styles, css`
      * {
        box-sizing: border-box;
      }

      :host {
        --pd-input-field-width: 330px;
      }
    `];
	}
	constructor() {
		super();
		this.clickableIcon = false;
		this.iconLabel = "";
		this._inputType = 8;
	}
	render() {
		const inputId = `${this.id}Input`;
		const errorId = `${this.id}Error`;
		return html`
      ${this._renderLabel(inputId)}
      <div
        class="${classMap(this.getClassmap({ input: true }))}"
      >
        <!-- Feld vor Icon: Das In-Feld-Icon ist absolut positioniert, die
             DOM-Reihenfolge hat also keinen visuellen Effekt — wohl aber
             auf die Tab-Reihenfolge. Stünde das Icon zuerst, landete man
             beim Tabben darauf, bevor man im Eingabefeld ist. -->
        <input
          id="${inputId}"
          name="${this.name || this.valueName || this.autoCompleteName}"
          class="input-style ${this.gradient ? "gradient" : ""}"
          type="file"
          maxlength="${this.maxlength ?? ""}"
          accept="${this.accept ?? ""}"
          ?disabled="${this.disabled}"
          aria-invalid="${this._invalid}"
          aria-describedby="${this._errorMsg ? errorId : ""}"
          @keyup="${this._onKeyUp}"
          @blur="${this._onBlur}"
          @focus="${this._onFocus}"
          @change="${this._onChange}"
        />
        ${this.icon ? html`
                <pd-icon
                  icon="${this.icon}"
                  ?activeIcon="${this.clickableIcon}"
                  class="infield-icon"
                  tabindex="${this.clickableIcon ? 0 : -1}"
                  role="${this.clickableIcon ? "button" : "img"}"
                  aria-label="${this.clickableIcon ? this.iconLabel : ""}"
                  aria-hidden="${!this.clickableIcon}"
                  @click="${this.clickableIcon ? this._iconClicked : null}"
                  @keydown="${this.clickableIcon ? this._onIconKeyDown : null}"
                ></pd-icon>
              ` : nothing}
      </div>
      ${this._renderErrorMsg(errorId)}
    `;
	}
	_iconClicked() {
		this.dispatchEvent(new CustomEvent("input-icon-click", {
			bubbles: true,
			composed: true
		}));
	}
	_onIconKeyDown(event) {
		if (event.key === " " || event.key === "Enter") {
			event.preventDefault();
			this._iconClicked();
		}
	}
	_onChange(e) {
		this._handleChangedValue(this._input.value, e, true);
	}
	/** Returns the selected files from the file input element. */
	get files() {
		return this._input?.files;
	}
	reset() {
		this._input.value = "";
		super.reset();
	}
	clear() {
		this.reset();
	}
};
__decorate([property({ type: String })], PdInputFile.prototype, "icon", void 0);
__decorate([property({ type: Boolean })], PdInputFile.prototype, "clickableIcon", void 0);
__decorate([property({ type: String })], PdInputFile.prototype, "iconLabel", void 0);
__decorate([property({ type: String })], PdInputFile.prototype, "accept", void 0);
__decorate([property({ type: Number })], PdInputFile.prototype, "maxlength", void 0);
//#endregion
export { PdInputFile };
